import { useState, useContext } from "react"
import { Field } from "formik"
import { useLazyQuery, gql } from "@apollo/client"
import { FormContext } from "../../context/formContext"
import { UserContext } from "../../context/userContext"
import PhoneInput from "react-phone-number-input/input"
import { LogError } from "../../helpers/logger"
import DescriptionTooltip from "./helpers/DescriptionTooltip"
import 'react-phone-number-input/style.css'
import styles from "../../styles/Form.module.css"

const PHONE_NUMBER_VERIFIER = gql`
  query phoneNumberVerifier(
    $participant_phone_primary: String!
    $organization_id: String!
    $participant_id: String!
  ) {
    phoneNumberVerifier(
      participant_phone_primary: $participant_phone_primary
      organization_id: $organization_id
      participant_id: $participant_id
    ) {
      data
    }
  }
`

const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false
  const cleanedNumber = phoneNumber.replace(/\D/g, "")
  return phoneNumber.startsWith("+1") && cleanedNumber.length === 11
}

const validatePhoneNumber = async (
  value,
  checkPhone,
  typeQuery,
  userCtx,
  organization_id,
) => {
  let error
  if (!isValidPhoneNumber(value)) {
    error = "Please enter a valid US phone number (maximum 11 digits)"
  } else {
    try {
      if (typeQuery !== "") {
        let checkPhoneVars = {
          participant_phone_primary: value,
          organization_id: organization_id,
          participant_id: userCtx.participant_id,
        }

        let user = userCtx.participant_id

        if (user !== undefined) {
          const result = await checkPhone({
            variables: checkPhoneVars,
          })

          if (
            result.data &&
            JSON.parse(result.data["phoneNumberVerifier"].data)
          ) {
            error = `${value} is already in use by an existing participant member. Please provide a unique phone number.`
          }
        }
      }
    } catch (error) {
      LogError("GraphQL query error => An error occurred while validating the phone number:", error)
    }
  }
  return error
}

export default function TypeTel({ field, block, index, organization_id }) {
  const [errorMsg, setErrorMsg] = useState(null)
  const [isValid, setIsValid] = useState(false)
  const userCtx = useContext(UserContext)
  const form = useContext(FormContext)
  const isDisabled = form ? form.blocksSaved[block] : false

  organization_id = userCtx.checkOrgPropWithFallback("organization_id", organization_id, "TypeTel")

  const [checkPhone, { loading }] = useLazyQuery(PHONE_NUMBER_VERIFIER, {
    onError: (error) => {
      LogError("GraphQL query error:", error)
      setErrorMsg(error.message)
      setIsValid(false)
    },
  })

  const handleBlur = async (formikField, form) => {
    form.setFieldTouched(formikField.name)
    console.log(formikField.name)
    const error = await validatePhoneNumber(
      formikField.value,
      checkPhone,
      formikField.name === "participant_phone_primary" ? "participant" : "",
      userCtx,
      organization_id
    )
    if (error) {
      setErrorMsg(error)
      setIsValid(false)
    } else {
      setErrorMsg(null)
      setIsValid(true)
    }
  }

  const isRequired = JSON.parse(field.field_validation.required[0])

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label} style={{ gap: "10px" }}>
        <span>
          {field.field_label}
          {isRequired && <span className={styles.asterisk}> *</span>}
        </span>
        {field.field_description &&
          <DescriptionTooltip
            fieldName={field.field_name}
            fieldDescription={field.field_description}
          />
        }
      </label>
      <Field
        name={field.field_name}
        validate={(value) =>
          isRequired
            ? validatePhoneNumber(value, checkPhone, field.field_name === "participant_phone_primary" ? "participant" : "", userCtx, organization_id)
            : undefined
        }
      >
        {({ field: formikField, meta, form }) => (
          <>
            <PhoneInput
              country="US"
              placeholder="Enter phone number"
              disabled={isDisabled || loading}
              value={formikField.value}
              onChange={(value) => {
                if (value && !value.startsWith("+1")) {
                  value = "+1" + value
                }
                form.setFieldValue(formikField.name, value)
                setErrorMsg(null)
                setIsValid(false)
              }}
              onBlur={() => handleBlur(formikField, form)}
              className="p-inputtext p-component"
              international={false}
            />
            {meta.touched && errorMsg && (
              <div className={styles.errFieldMsg}>{errorMsg}</div>
            )}
            {!meta.error && isValid && (
              <div className={styles.validFieldMsg}>
                {formikField.value} is a valid US phone number
              </div>
            )}
          </>
        )}
      </Field>
    </div>
  )
}
